import { useState } from 'react';
import { Card, CardHeader, InlineStat } from '../../../components/ui/Card';
import { Table, THead, Th, Td, Tr, TTotalRow, TableFooter } from '../../../components/ui/Table';
import { Badge } from '../../../components/ui/Badge';
import { ChartEmpty } from '../../../components/ui/chart';
import { formatDayMonth } from '../../../utils/dateFormat';
import type { GtmBoardData, GtmDealSourceDeal, GtmDealSourceEvidence } from '../../../api/gtm';
import { fmtCurrency, fmtNumber, fmtPercent } from './components/format';

/**
 * No trend line sits on this card, so nothing draws the "Attribution logic
 * changed" marker yet. If a date axis is added to the pipeline figures, draw a
 * ReferenceLine at this date the way RELAUNCH_DATE is drawn on the trend charts.
 * The API sends the same date as `dealSources.attributionChangedDate`.
 */
export const ATTRIBUTION_CHANGED_DATE = '2026-10-15';

/** "Below this share of new-business value with a known source" the card warns. */
const KNOWN_SOURCE_WARN = 0.7;

const EVIDENCE_PREFIX: Record<GtmDealSourceEvidence['kind'], string> = {
  inbound_conversion: 'Inbound',
  outbound_touch: 'Outbound first',
  lead_source: 'Deal Lead Source',
  account_source: 'Company Account Source',
  deal_type: 'Deal type',
};

/** One evidence item as a sentence: "Inbound: Request demo, utm linkedin / paid by x@co.com on 3 Aug". */
function evidenceLine(e: GtmDealSourceEvidence): string {
  const by = e.email ? ` by ${e.email}` : '';
  const on = e.at ? ` on ${formatDayMonth(e.at)}` : '';
  return `${EVIDENCE_PREFIX[e.kind]}: ${e.label}${by}${on}`;
}

const ROLE_LABEL: Record<GtmDealSourceDeal['role'], string | null> = {
  sourced: 'Marketing sourced',
  claimed: 'per sales field, no tracked conversion',
  influenced: 'Marketing influenced',
  none: null,
};

function DealList({ deals, showMotion, motionLabels }: { deals: GtmDealSourceDeal[]; showMotion: boolean; motionLabels: Map<string, string> }) {
  return (
    <ul className="mt-2 divide-y divide-border-light">
      {deals.map((d, i) => {
        const role = ROLE_LABEL[d.role];
        return (
          <li key={`${d.name}-${i}`} className="py-2">
            <div className="flex items-baseline justify-between gap-3">
              <span className="min-w-0 break-words">
                {d.attioUrl ? (
                  <a href={d.attioUrl} target="_blank" rel="noreferrer" className="text-accent hover:underline">
                    {d.name}
                  </a>
                ) : (
                  <span className="text-text-primary">{d.name}</span>
                )}
                <span className="text-body-xs text-text-tertiary"> · {d.companyName ?? 'no company on the deal'}</span>
              </span>
              <span className="shrink-0 tabular-nums text-text-secondary">{fmtCurrency(d.value)}</span>
            </div>
            <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-body-xs text-text-tertiary">
              {showMotion && d.motion && <Badge>{motionLabels.get(d.motion) ?? d.motion}</Badge>}
              {role && <Badge variant={d.role === 'sourced' ? 'success' : d.role === 'claimed' ? 'default' : 'info'}>{role}</Badge>}
              {d.conflict && (
                <Badge variant="warning">
                  check: outbound touch on {formatDayMonth(d.evidence.find((e) => e.kind === 'outbound_touch')?.at ?? '')}
                </Badge>
              )}
              {d.needsClarification && <Badge variant="warning">{d.needsClarification}</Badge>}
              {d.untyped && <Badge variant="warning">no deal type in Attio</Badge>}
            </div>
            {d.evidence.length === 0 ? (
              <p className="mt-0.5 text-body-xs text-text-tertiary">No form, outreach or sales field found.</p>
            ) : (
              d.evidence.map((e, j) => (
                <p key={j} className="mt-0.5 text-body-xs text-text-secondary break-words">
                  {evidenceLine(e)}
                </p>
              ))
            )}
          </li>
        );
      })}
    </ul>
  );
}

/**
 * Pipeline created, by how each deal started. New business is split by motion
 * (decided from forms, outreach and the Attio sales fields, see the footer);
 * expansions and renewals are one group that marketing can influence but never
 * source. Every row opens to its deals and the evidence behind each.
 */
export function DealSourcesCard({ data }: { data: GtmBoardData }) {
  const d = data.dealSources;
  const [open, setOpen] = useState<string | null>(null);
  const toggle = (key: string) => setOpen((cur) => (cur === key ? null : key));

  if (!d || d.newBusinessDeals + d.expansionRenewal.dealCount === 0) {
    return (
      <Card>
        <CardHeader title="Pipeline created" description="Deals opened in the period, open + won, by how they started (Attio)" />
        <ChartEmpty label="No deals opened in this period." />
      </Card>
    );
  }

  const total = d.newBusinessValue + d.expansionRenewal.pipelineValue;
  const dealsTotal = d.newBusinessDeals + d.expansionRenewal.dealCount;
  const known = d.knownSourceShare;
  const lowKnown = known !== null && known < KNOWN_SOURCE_WARN;
  const lookback = d.lookbackMonths;

  const toggleButton = (key: string, label: string) => (
    <button
      type="button"
      onClick={() => toggle(key)}
      aria-expanded={open === key}
      className="text-left text-text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
    >
      <span aria-hidden="true" className="text-text-tertiary">{open === key ? '▾ ' : '▸ '}</span>
      {label}
    </button>
  );

  return (
    <Card>
      <CardHeader
        title="Pipeline created"
        description="Deals opened in the period, open + won, by how they started (Attio, forms, lemlist)"
        action={
          lowKnown ? <Badge variant="warning">{fmtPercent((known ?? 0) * 100, 0)} of new business has a known source</Badge> : undefined
        }
      />
      {d.dataWarnings.length > 0 && (
        <div className="mb-4 space-y-1">
          {d.dataWarnings.map((w) => (
            <p key={w} className="text-body-sm text-warning">{w}</p>
          ))}
        </div>
      )}
      <div className="grid grid-cols-2 gap-6 mb-4 lg:grid-cols-4">
        <InlineStat
          label="Inbound-sourced"
          value={fmtCurrency(d.sourcedValue)}
          sub={`${fmtNumber(d.sourcedDeals)} new-business ${d.sourcedDeals === 1 ? 'deal' : 'deals'} with a tracked conversion`}
        />
        <InlineStat
          label="Claimed inbound"
          value={fmtCurrency(d.claimedValue)}
          sub={`${fmtNumber(d.claimedDeals)} ${d.claimedDeals === 1 ? 'deal' : 'deals'} per sales field, no tracked conversion${d.claimedConflictDeals > 0 ? `; ${fmtNumber(d.claimedConflictDeals)} to check` : ''}`}
        />
        <InlineStat
          label="Known source"
          value={known === null ? '–' : fmtPercent(known * 100, 0)}
          sub={`of ${fmtCurrency(d.newBusinessValue)} new business`}
        />
        <InlineStat
          label="Marketing influenced"
          value={fmtCurrency(d.influencedValue)}
          sub={`${fmtNumber(d.influencedDeals)} ${d.influencedDeals === 1 ? 'deal' : 'deals'} touched, not sourced`}
        />
      </div>

      <Table colWidths={['auto', '7rem', '4rem']}>
        <THead>
          <tr>
            <Th>New business by motion</Th>
            <Th align="right">Pipeline</Th>
            <Th align="right">Deals</Th>
          </tr>
        </THead>
        <tbody>
          {d.newBusiness.length === 0 && (
            <Tr>
              <Td className="text-text-tertiary">No new-business deals</Td>
              <Td align="right">{fmtCurrency(0)}</Td>
              <Td align="right">0</Td>
            </Tr>
          )}
          {d.newBusiness.map((r) => (
            <Tr key={r.motion}>
              <Td className="text-text-primary">
                {toggleButton(r.motion, r.label)}
                {r.motion === 'inbound' && <span className="text-body-xs text-text-tertiary"> · tracked conversion or sales field, see each deal</span>}
                {r.motion === 'unknown' && d.newBusinessValue > 0 && lowKnown && (
                  <>
                    {' '}
                    <Badge variant="warning">{fmtPercent((r.pipelineValue / d.newBusinessValue) * 100, 0)} of new business</Badge>
                  </>
                )}
              </Td>
              <Td align="right">{fmtCurrency(r.pipelineValue)}</Td>
              <Td align="right">{fmtNumber(r.dealCount)}</Td>
            </Tr>
          ))}
          {d.expansionRenewal.dealCount > 0 && (
            <Tr>
              <Td className="text-text-primary">
                {toggleButton('expansion_renewal', 'Expansion & renewal')}
                <span className="text-body-xs text-text-tertiary"> · influenced, not sourced</span>
              </Td>
              <Td align="right">{fmtCurrency(d.expansionRenewal.pipelineValue)}</Td>
              <Td align="right">{fmtNumber(d.expansionRenewal.dealCount)}</Td>
            </Tr>
          )}
          <TTotalRow>
            <Td>Pipeline created</Td>
            <Td align="right">{fmtCurrency(total)}</Td>
            <Td align="right">{fmtNumber(dealsTotal)}</Td>
          </TTotalRow>
        </tbody>
      </Table>

      {open && (
        <div className="mt-3 text-body-sm">
          <DealList
            deals={open === 'expansion_renewal' ? d.expansionRenewal.deals : (d.newBusiness.find((r) => r.motion === open)?.deals ?? [])}
            showMotion={false}
            motionLabels={new Map(d.newBusiness.map((r) => [r.motion, r.label]))}
          />
        </div>
      )}

      <TableFooter>
        Each deal is read from what happened at its company in the {fmtNumber(lookback)} months before it was opened.
        A website, LinkedIn or Calendly form from anyone there makes it inbound, unless a rep had already reached out
        (lemlist, an SDR form, a meeting): then it is outbound and marketing only influenced it. With no form, the
        deal’s Lead Source in Attio decides, then the company’s Account Source, then any outreach found; an inbound
        field with no tracked conversion is “claimed”, kept out of the sourced figure. An explicit outbound Lead Source
        beats a form older than 90 days. A Lead Source of “LinkedIn” stays unknown until a lead form shows up. ABM-tagged
        deals stay ABM. Renewals and expansions (Attio type, or Lead Source “Customer Expansion”) are listed apart and
        never counted as sourced.
        {d.untypedDeals > 0 &&
          ` ${fmtNumber(d.untypedDeals)} new-business deals (${fmtCurrency(d.untypedValue)}) have no deal type in Attio and are counted as new.`}
        {d.lostValue > 0 && ` Lost deals (${fmtCurrency(d.lostValue)}) are left out.`} The source comes from this data,
        not from the Lead channel on the company: setting a Lead Source on the deal in Attio changes the result only when
        no form or outreach decided it. Apollo SDR calls are not included (they carry no contact or company). Logic
        changed on {formatDayMonth(ATTRIBUTION_CHANGED_DATE)}; earlier figures used the company’s lead channel.
      </TableFooter>
    </Card>
  );
}
